<article class="brandbot-card no-hover flex flex-col h-full rounded-2xl overflow-hidden">
  <div class="px-5 pt-5 pb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
    <h3
      class="text-pc-eyebrow font-bold uppercase tracking-widest leading-tight text-slate-500 dark:text-neutral-500"
    >
      {{ 'COMMERCIAL_FUNNEL' | translate }}
    </h3>
    <!-- States the window, does not set it: the control lives on the card that
         heads this block, and one payload feeds all three. -->
    @if (windowLabel(); as window) {
      <span class="text-pc-eyebrow font-medium tabular-nums text-[color:var(--text-subtitle)]">
        {{ window }}
      </span>
    }
  </div>

  @if (hasData()) {
    <!-- The hero: everything closed in the window. One eyebrow on the card — "ganado"
         qualifies the amount in running text instead of stacking a second header. -->
    <div class="px-5 pb-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
      @if (wonLabel(); as won) {
        <span
          class="text-[22px] 2xl:text-[27px] font-semibold tracking-tight tabular-nums leading-none text-[color:var(--text-title)]"
        >
          {{ won }}
        </span>
      } @else {
        <!-- Money exists but not in this card's currency (the footer names the
             other one). A hero reading "US$ 0" there would deny a real sale. -->
        <span
          class="text-pc-title-sm font-medium italic leading-none text-[color:var(--text-subtitle)]"
        >
          {{ 'LOST_NO_AMOUNT' | translate }}
        </span>
      }
      <span
        class="inline-flex flex-wrap items-baseline gap-x-1.5 text-pc-body text-[color:var(--text-subtitle)]"
      >
        <span>{{ 'FUNNEL_WON_INLINE' | translate }}</span>
        @if (wonOrdersKey(); as key) {
          <span aria-hidden="true">·</span>
          <span>{{ key | translate: { count: orderCount() } }}</span>
        }
      </span>
    </div>

    <!-- The card's verdict on the cohort: the transition that lost the most, named by
         where it happens and stated as the loss itself. -->
    <div
      class="mx-5 mt-1 mb-3 pt-3 min-h-[1.25rem] border-t border-neutral-200/60 dark:border-white/10 flex flex-wrap items-baseline gap-x-1.5 gap-y-1 text-pc-secondary text-[color:var(--text-subtitle)]"
    >
      @if (biggestDrop(); as drop) {
        <span class="opacity-70 whitespace-nowrap">{{
          'FUNNEL_BIGGEST_DROP_LEAD' | translate
        }}</span>
        <span class="font-semibold whitespace-nowrap text-[color:var(--text-title)]">
          {{ drop.stepKey | translate }}
        </span>
        <span class="tabular-nums">
          —
          {{
            drop.detailKey | translate: { lost: drop.lost, from: drop.from, percent: drop.percent }
          }}
        </span>
      } @else {
        <!-- Nothing was lost at any step: the cohort's own size is the fact. -->
        <span class="font-semibold whitespace-nowrap text-[color:var(--text-title)]">
          {{ 'STAGE_OPPORTUNITIES' | translate }}
        </span>
        <span class="tabular-nums whitespace-nowrap">
          {{ 'FUNNEL_ENTERED' | translate: { count: cohort() } }}
        </span>
      }
    </div>

    <!-- The ledger, at the card's full width. Each main stage line carries a bar whose
         width is its share of the cohort, so the shape of the funnel is read in the rows
         themselves rather than in a drawing beside them. -->
    <div class="funnel-figures px-5 pb-3 pt-1">
      <div
        class="funnel-row funnel-heading text-pc-eyebrow font-bold uppercase tracking-widest leading-tight text-slate-500 dark:text-neutral-500"
      >
        <span class="funnel-col-name">
          <span class="funnel-node-label">{{ 'FUNNEL_COL_STAGE' | translate }}</span>
        </span>
        <!-- Says what the percentage is OF: every share on the card divides by the cohort. -->
        <span class="funnel-col-share">
          {{ 'FUNNEL_COL_SHARE_OF' | translate: { total: cohort() } }}
        </span>
        <span class="funnel-col-count">{{ 'FUNNEL_COL_COUNT' | translate }}</span>
        <span class="funnel-col-amount">{{ 'FUNNEL_COL_AMOUNT' | translate }}</span>
      </div>

      <div class="funnel-tree">
        @for (node of ledger(); track node.titleKey) {
          <ng-container *ngTemplateOutlet="ledgerRow; context: { $implicit: node }"></ng-container>
        }
      </div>

      <!-- Orders closed in the window, behind a heavier rule: not a slice of the cohort.
           The subtotal IS the hero figure, and the three lines under it are siblings
           that add up to it. -->
      @if (orderLedger().length) {
        <div class="funnel-orders">
          @for (node of orderLedger(); track node.titleKey) {
            <ng-container
              *ngTemplateOutlet="ledgerRow; context: { $implicit: node }"
            ></ng-container>
          }
        </div>
      }
    </div>

    <!-- Defect reports about the data, rendered only when there is one. -->
    @if (unpricedOrders() || otherCurrency()) {
      <div class="mt-auto px-5 py-3 border-t border-neutral-200/60 dark:border-white/10">
        @if (unpricedOrders(); as unpriced) {
          <p class="text-pc-label text-[color:var(--text-subtitle)]">
            {{ 'FUNNEL_UNPRICED_ORDERS' | translate: unpriced }}
          </p>
        }
        @if (otherCurrency(); as other) {
          <p
            class="text-pc-label text-[color:var(--text-subtitle)]"
            [class.mt-1]="unpricedOrders() !== null"
          >
            {{ 'SECOND_CURRENCY_NOTE' | translate: { isoCode: other } }}
          </p>
        }
      </div>
    }
  } @else if (loading()) {
    <div
      class="pc-skeleton-card relative px-5 pb-5 flex-1 min-h-0 flex items-center justify-center"
      aria-hidden="true"
    >
      <div class="pc-skeleton-aurora"></div>
      <div class="pc-skeleton-ring"></div>
      <div class="relative z-10 w-full h-full flex items-center justify-center">
        <div class="pc-skeleton-tile">
          <div class="pc-skeleton-tile-core"></div>
        </div>
      </div>
    </div>
  } @else if (error()) {
    <div class="px-5 pb-5 flex-1 min-h-0 flex items-center justify-center">
      <primecontrol-empty-data-particules
        [message]="'DASHBOARD_LOAD_ERROR'"
        [icon]="'heroicons_outline:exclamation-circle'"
      ></primecontrol-empty-data-particules>
    </div>
  } @else {
    <!-- Full-size state, same as the closed-business card above. -->
    <div class="px-5 pb-5 flex-1 min-h-0 flex items-center justify-center">
      <primecontrol-empty-data-particules
        [message]="'NO_FUNNEL_DATA'"
        [icon]="'heroicons_outline:funnel'"
      ></primecontrol-empty-data-particules>
    </div>
  }
</article>

<!-- One ledger line, shared by the cohort tree and the orders block. -->
<ng-template #ledgerRow let-node>
  <a
    [routerLink]="node.route ? [node.route] : null"
    class="funnel-row funnel-node group"
    [class.funnel-node--d1]="node.depth === 1"
    [class.funnel-node--d2]="node.depth === 2"
  >
    <span class="funnel-col-name">
      <span class="funnel-node-label" [title]="node.titleKey | translate">
        {{ node.titleKey | translate }}
      </span>
      <!-- Not `aria-hidden`: the hint is the only place this line's meaning is written
           down, so it has to be readable without a pointer. -->
      @if (node.hintKey; as hint) {
        <primecontrol-svg-icon
          class="funnel-node-hint"
          [svgIcon]="'info'"
          [size]="13"
          [pcTooltip]="hint | translate"
          role="img"
          [attr.aria-label]="hint | translate"
        ></primecontrol-svg-icon>
      }
    </span>

    <!-- The line's share of the cohort, drawn. Decorative: the figure is in the % cell. -->
    @if (node.barPct !== null) {
      <span class="funnel-col-bar" aria-hidden="true">
        <span class="funnel-bar-fill" [style.width.%]="node.barPct"></span>
      </span>
    }

    <span class="funnel-col-share funnel-node-figure">
      @if (node.sharePct !== null) {
        {{ node.sharePct }}%
      }
    </span>

    <span class="funnel-col-count funnel-node-figure funnel-node-count">{{ node.count }}</span>

    @if (node.amount; as amount) {
      <span class="funnel-col-amount funnel-node-figure funnel-node-amount">{{ amount }}</span>
    } @else if (node.carriesMoney) {
      <!-- Money that exists but not in this card's currency. Never a zero. -->
      <span class="funnel-col-amount funnel-node-figure funnel-node-amount italic opacity-70">
        {{ 'LOST_NO_AMOUNT' | translate }}
      </span>
    } @else {
      <!-- An opportunity carries no monetary field: blank, not captioned. -->
      <span class="funnel-col-amount funnel-node-amount" aria-hidden="true"></span>
    }
  </a>
</ng-template>
